// Session 49 (Wave 14): the Learn more page for a locked service, and the way
// an existing client adds it to their plan without emailing Leena.
//
// /learn/<group_code>. What it says comes from three places: the catalogue
// (summary, bullets, benefit, price: approved with the sign-up page), the
// "how it works" copy in src/learn/copy.js (drafted for Leena's approval), and
// portal_addon_quote, which names the one plan this client would be offered
// (the newsletter by their blog volume), whether they already have it, whether
// it comes with something they lack, and the subscription it would join.
//
// The browser never names a plan or a price. "Add to my plan" asks the
// stripe-addon function for a quote (what is charged today, what the new
// total is), shows it, and only then asks it to add. A client with a Stripe
// subscription gets the price added to it, prorated, no card re-entry (pack
// 10); one without is taken to Stripe Checkout for that service alone, no
// trial (pack 9). Either way Stripe's money is what switches the module on,
// through portal_grant_plan_entitlements, never this page.
//
// The agency sees the same page for the selected client, with the switch in
// client settings in place of the button: Leena is not her clients' card.
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { clientPath } from '../lib/clientLink.js';
import { HOW_IT_WORKS } from '../learn/copy.js';
import { TEMPLATES, loadSample, loadTemplate, previewSlots, render } from '../lib/newsletter.js';
import { newsletterFromPack } from '../components/NewsletterSetup.jsx';

const money = (cents) => {
  const d = (cents || 0) / 100;
  return '$' + (Number.isInteger(d) ? d.toFixed(0) : d.toFixed(2));
};
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : '');

// Session 49 review 8: Analytics is not sold on its own (it comes with
// everything except a courses-only plan, Session 37), so the catalogue has no
// group for it and the Learn page explains that instead of offering a price.
const NOT_SOLD_ALONE = {
  analytics: {
    name: 'Analytics',
    summary: 'Your website and Google search results in one place.',
    benefit: 'See what your blogs and your search work are doing, without logging into three tools.',
    details: ['Comes with every plan except a course on its own: add blogs, the SEO review or the Google Business Profile review and analytics switches on with it.'],
  },
};

// Where the service lives once it is on, so "You have this already" can point there.
const HOME_PATH = {
  blogs: '/plan', email: '/newsletters', seo: '/seo-recommendations',
  gmb: '/gmb-audit', courses: '/courses/ai_at_work_101', analytics: '/results',
};

export default function Learn() {
  const { group: groupCode } = useParams();
  const navigate = useNavigate();
  const { me, isAgency, activeId, activeClient, reloadClients } = useAuth();
  const [group, setGroup] = useState(null);
  const [plans, setPlans] = useState([]);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [interval, setInterval_] = useState('monthly');
  const [preview, setPreview] = useState(null);   // the stripe-addon quote, before confirming
  const [busy, setBusy] = useState(false);
  const [examples, setExamples] = useState(null);   // the four Digital Lotus newsletters, for the email group
  const [banner, setBanner] = useState(() => (
    new URLSearchParams(window.location.search).get('cancelled') === '1'
      ? { tone: 'info', text: 'No payment was taken. Nothing has changed on your plan.' } : null
  ));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setGroup(null); setQuote(null); setError(null); setPreview(null);
      const [g, p] = await Promise.all([
        supabase.from('v_portal_plan_groups').select('*').eq('group_code', groupCode).maybeSingle(),
        supabase.from('v_portal_plans').select('*').eq('group_code', groupCode).order('sort'),
      ]);
      if (cancelled) return;
      if (g.error || p.error) { setError((g.error || p.error).message); return; }
      if (!g.data && NOT_SOLD_ALONE[groupCode]) { setGroup({ ...NOT_SOLD_ALONE[groupCode], group_code: groupCode, not_sold_alone: true }); return; }
      if (!g.data) { setError('We do not offer that one.'); return; }
      setGroup(g.data); setPlans(p.data || []);
      if (activeId) {
        const { data, error: qe } = await supabase.rpc('portal_addon_quote', { p_client_id: activeId, p_group_code: groupCode });
        if (cancelled) return;
        if (qe) setError(qe.message); else { setQuote(data); if (data?.current_interval) setInterval_(data.current_interval); }
      }
    })();
    return () => { cancelled = true; };
  }, [groupCode, activeId]);

  // The same four finished Digital Lotus newsletters the onboarding cards show,
  // rendered here from the sample, sandboxed (no scripts, opaque origin).
  useEffect(() => {
    if (!HOW_IT_WORKS[groupCode]?.sample) { setExamples(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const [sample, ...htmls] = await Promise.all([loadSample(), ...TEMPLATES.map(([t]) => loadTemplate(t))]);
        if (cancelled) return;
        const v = newsletterFromPack(null, null);
        setExamples(TEMPLATES.map(([t], i) => [t, render(htmls[i], previewSlots(sample, t, { ...v, template: t }, { name: 'Digital Lotus Marketing' }, { useSample: true }))]));
      } catch { if (!cancelled) setExamples([]); }
    })();
    return () => { cancelled = true; };
  }, [groupCode]);

  async function askStripe(action) {
    const { data, error: fe } = await supabase.functions.invoke('stripe-addon', {
      body: { action, group_code: groupCode, interval },
    });
    if (fe) {
      // The function's own sentence when it sent one; the SDK's otherwise.
      let msg = fe.message;
      try { const j = await fe.context?.json?.(); if (j?.error) msg = j.error; } catch { /* keep msg */ }
      throw new Error(msg);
    }
    return data;
  }

  async function onPreview() {
    setBusy(true); setBanner(null);
    try { setPreview(await askStripe('quote')); }
    catch (e) { setBanner({ tone: 'err', text: e.message }); }
    setBusy(false);
  }

  async function onConfirm() {
    setBusy(true); setBanner(null);
    try {
      const r = await askStripe('add');
      if (r?.url) { window.location.assign(r.url); return; }
      if (r?.added) {
        await reloadClients?.();
        navigate(clientPath(`/welcome?added=${groupCode}`, activeId));
        return;
      }
      throw new Error('Stripe did not say what happened. Nothing has been charged twice; please check your email or try again.');
    } catch (e) {
      setBanner({ tone: 'err', text: e.message });
      setBusy(false);
    }
  }

  if (error) return <div className="empty"><h2>Couldn't load this</h2><p>{error}</p></div>;
  if (!group) return <p className="lede">Loading…</p>;

  const how = HOW_IT_WORKS[groupCode];
  const price = quote ? (interval === 'annual' ? quote.price_annual_cents : quote.price_monthly_cents) : null;
  const per = interval === 'annual' ? 'a year' : 'a month';
  const home = HOME_PATH[groupCode] || '/';

  return (
    <div className="learn-page">
      <h1>{group.name}</h1>
      <p className="lede">{group.summary}</p>
      {group.benefit && <p className="learn-benefit"><span aria-hidden="true">→ </span>{group.benefit}</p>}
      {(group.details || []).length > 0 && (
        <ul className="learn-details">{group.details.map((d, i) => <li key={i}>{d}</li>)}</ul>
      )}

      {how && (
        <section className="review-section learn-how">
          <h2>{how.heading}</h2>
          <ol>{how.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
          {how.after && <p className="hint">{how.after}</p>}
          {how.sample && examples && examples.length > 0 && (
            <div className="learn-samples">
              {examples.map(([t, html]) => (
                <figure key={t} className="learn-sample">
                  <div className="learn-sample-frame">
                    <iframe title={`${t} example`} srcDoc={html} sandbox="" scrolling="no" loading="lazy" />
                  </div>
                  <figcaption>{t[0].toUpperCase() + t.slice(1)}</figcaption>
                </figure>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="review-section learn-buy">
        {banner && <div className={`banner banner-${banner.tone === 'err' ? 'err' : 'info'}`}>{banner.text}</div>}

        {group.not_sold_alone && (
          <p className="hint">
            Not sold on its own. <Link to={clientPath('/learn/blogs', activeId)}>See the blog plans</Link>,{' '}
            <Link to={clientPath('/learn/seo', activeId)}>the SEO review</Link> or{' '}
            <Link to={clientPath('/learn/gmb', activeId)}>the Google Business Profile review</Link>.
          </p>
        )}
        {!group.not_sold_alone && !activeId && isAgency && (
          <p className="hint">Pick a client in the switcher to see what they would be offered.</p>
        )}
        {!group.not_sold_alone && activeId && !quote && <p className="hint">Loading the price…</p>}

        {quote && quote.already_on && (
          <>
            <h2>You have this already</h2>
            <p><Link to={clientPath(home, activeId)}>Open {group.name}</Link></p>
          </>
        )}

        {quote && !quote.already_on && (
          <>
            <h2>
              {quote.plan_name}
              <span className="learn-price">{money(price)} <span className="signup-option-per">{per}</span></span>
            </h2>
            {quote.plan_code !== groupCode && quote.blurb && <p className="hint">{quote.blurb}</p>}

            {!quote.group_sellable && (
              <p className="hint">Not open yet. We are still building this one.</p>
            )}

            {quote.group_sellable && !quote.requires_ok && (
              <p className="hint">
                {group.name} comes with {quote.requires_name}. Add that first:{' '}
                <Link to={clientPath('/learn/blogs', activeId)}>see the blog plans</Link>.
              </p>
            )}

            {quote.group_sellable && quote.requires_ok && isAgency && (
              <p className="hint">
                This is what {activeClient?.name || 'the client'} would see. To switch it on for them yourself,{' '}
                <Link to={`/admin/clients/${activeId}/settings?client=${activeId}`}>open their settings</Link>.
              </p>
            )}

            {quote.group_sellable && quote.requires_ok && !isAgency && !quote.checkout_ready && (
              <p className="hint">
                This one is not quite ready to buy online. Email{' '}
                <a href={`mailto:leena@digitallotusmarketing.com?subject=${encodeURIComponent(`Add ${group.name} to our plan`)}`}>leena@digitallotusmarketing.com</a>{' '}
                and we will add it by hand.
              </p>
            )}

            {quote.group_sellable && quote.requires_ok && !isAgency && quote.checkout_ready && !preview && (
              <>
                {!quote.subscription_id && (
                  <div className="signup-interval" role="group" aria-label="How often you pay">
                    <button type="button" className={`btn ${interval === 'monthly' ? 'btn-primary' : ''}`}
                            aria-pressed={interval === 'monthly'} onClick={() => setInterval_('monthly')}>Monthly</button>
                    <button type="button" className={`btn ${interval === 'annual' ? 'btn-primary' : ''}`}
                            aria-pressed={interval === 'annual'} onClick={() => setInterval_('annual')}>
                      Yearly <span className="signup-save-pill">2 months free</span>
                    </button>
                  </div>
                )}
                {quote.subscription_id && (
                  <p className="hint">
                    Added to the plan you already pay {quote.current_interval === 'annual' ? 'yearly' : 'monthly'}. No card to enter again.
                    {quote.subscription_status === 'trialing' ? ' Your plan is still in its free trial, so this is billed from the day the trial ends.' : ''}
                  </p>
                )}
                <div className="review-actions" style={{ justifyContent: 'flex-start' }}>
                  <button type="button" className="btn btn-primary" disabled={busy} onClick={onPreview}>
                    {busy ? 'One moment…' : 'Add to my plan'}
                  </button>
                </div>
                <p className="hint">Nothing is charged until you confirm on the next step.</p>
              </>
            )}

            {preview && (
              <div className="learn-confirm">
                <h3>Confirm</h3>
                {preview.mode === 'add_to_subscription' ? (
                  <ul>
                    {preview.in_trial ? (
                      <li><strong>Today:</strong> nothing. Your plan is in its free trial until {fmtDay(preview.trial_end)}; this is billed from then, with the rest.</li>
                    ) : (
                      <li><strong>Today:</strong> {money(preview.due_today_cents)}, the rest of this billing period, charged now to the card on your plan.</li>
                    )}
                    <li><strong>Then:</strong> {money(preview.new_total_cents)} {preview.interval === 'annual' ? 'a year' : 'a month'} from {fmtDay(preview.next_charge_at)}, for everything on your plan.</li>
                    <li>No separate trial on an addition. Stripe emails you a receipt.</li>
                  </ul>
                ) : (
                  <ul>
                    <li><strong>{money(price)} {per}</strong>, first charge today. No trial on an addition.</li>
                    <li>You will be taken to our card page, run by Stripe. We never see or store the number.</li>
                  </ul>
                )}
                <div className="review-actions" style={{ justifyContent: 'flex-start' }}>
                  <button type="button" className="btn btn-primary" disabled={busy} onClick={onConfirm}>
                    {busy ? 'Adding…' : (preview.mode === 'add_to_subscription' ? `Confirm, add ${quote.plan_name}` : 'Continue to payment')}
                  </button>
                  <button type="button" className="link-btn" disabled={busy} onClick={() => setPreview(null)}>Not now</button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
